/**
 * Chord transposition utility.
 * Transposes chord names (e.g. Em, C, D, G, Am7) by semitones.
 */

const CHROMATIC_SHARP = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const CHROMATIC_FLAT = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

const ROOT_TO_INDEX: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3, E: 4, F: 5, "F#": 6, Gb: 6,
  G: 7, "G#": 8, Ab: 8, A: 9, "A#": 10, Bb: 10, B: 11,
};

/** Chord regex: root (A-G + optional #/b) + quality (m, maj, 7, etc.) + optional /bass */
const CHORD_REGEX = /([A-G])(#|b)?([mM]|maj|min|dim|aug|sus\d?|add\d?)?(\d*)(\/[A-G](#|b)?)?(?=[\s/()\n,.-]|$)/g;

function getRootIndex(root: string, accidental: string | undefined): number {
  const key = accidental ? root + accidental : root;
  return ROOT_TO_INDEX[key] ?? ROOT_TO_INDEX[root] ?? 0;
}

function indexToNote(index: number, preferFlat: boolean): string {
  const scale = preferFlat ? CHROMATIC_FLAT : CHROMATIC_SHARP;
  return scale[((index % 12) + 12) % 12];
}

/**
 * Transpose a single chord by semitones.
 */
export function transposeChord(chord: string, semitones: number): string {
  const match = chord.match(/^([A-G])(#|b)?([mM]|maj|min|dim|aug|sus\d?|add\d?)?(\d*)(\/[A-G](#|b)?)?/);
  if (!match) return chord;

  const [, root, accidental, quality, extension, slashBass] = match;
  const rootIdx = getRootIndex(root, accidental);
  const newRootIdx = (rootIdx + semitones + 12) % 12;
  const preferFlat = accidental === "b" || (slashBass && slashBass.includes("b"));
  const newRoot = indexToNote(newRootIdx, preferFlat);

  let result = newRoot + (quality || "") + (extension || "") + (slashBass || "");

  if (slashBass) {
    const bassMatch = slashBass.match(/\/([A-G])(#|b)?/);
    if (bassMatch) {
      const [, bassRoot, bassAcc] = bassMatch;
      const bassIdx = getRootIndex(bassRoot, bassAcc);
      const newBassIdx = (bassIdx + semitones + 12) % 12;
      const newBass = indexToNote(newBassIdx, bassAcc === "b");
      result = newRoot + (quality || "") + (extension || "") + "/" + newBass;
    }
  }

  return result;
}

/**
 * Transpose all chords in a chord chart (lyrics + chords) by semitones.
 */
export function transposeChords(text: string, semitones: number): string {
  if (semitones === 0) return text;

  return text.replace(CHORD_REGEX, (match) => transposeChord(match, semitones));
}
